import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../config/api';
import PageNavbar from './PageNavbar';
import './PosInventory.css';

function formatInr(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

export default function PosInventory() {
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [busyCategoryId, setBusyCategoryId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [addQtys, setAddQtys] = useState({});
  const [activeCategoryId, setActiveCategoryId] = useState('all');
  const [search, setSearch] = useState('');

  const loadStores = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/stores');
      const list = (data.stores || []).filter((s) => Number(s.is_active) !== 0);
      setStores(list);
      setStoreId((prev) => prev || (list[0] ? String(list[0].id) : ''));
    } catch (error) {
      setMsg({ type: 'error', text: error.response?.data?.error || 'Could not load stores' });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadInventory = useCallback(async (sid) => {
    if (!sid) {
      setItems([]);
      return;
    }
    setListLoading(true);
    setMsg(null);
    try {
      const { data } = await api.get('/pos/inventory', { params: { storeId: sid } });
      const list = data.items || [];
      setItems(list);
      const nextDrafts = {};
      list.forEach((row) => {
        nextDrafts[row.id] = String(row.stock_qty ?? 0);
      });
      setDrafts(nextDrafts);
      setAddQtys({});
    } catch (error) {
      setMsg({ type: 'error', text: error.response?.data?.error || 'Could not load inventory' });
      setItems([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  useEffect(() => {
    if (storeId) {
      setActiveCategoryId('all');
      setSearch('');
      loadInventory(storeId);
    }
  }, [storeId, loadInventory]);

  const categories = useMemo(() => {
    const map = new Map();
    items.forEach((row) => {
      const id = row.category_id;
      if (!map.has(id)) {
        map.set(id, {
          id,
          name: row.category_name || 'Uncategorized',
          products: []
        });
      }
      map.get(id).products.push(row);
    });
    return Array.from(map.values());
  }, [items]);

  useEffect(() => {
    if (activeCategoryId === 'all') return;
    if (!categories.some((c) => String(c.id) === String(activeCategoryId))) {
      setActiveCategoryId('all');
    }
  }, [categories, activeCategoryId]);

  const filteredCategories = useMemo(() => {
    const q = search.trim().toLowerCase();
    const scoped =
      activeCategoryId === 'all'
        ? categories
        : categories.filter((c) => String(c.id) === String(activeCategoryId));

    if (!q) return scoped;

    return scoped
      .map((cat) => ({
        ...cat,
        products: cat.products.filter(
          (p) =>
            (p.name || '').toLowerCase().includes(q) ||
            (p.shortcode || '').toLowerCase().includes(q)
        )
      }))
      .filter((cat) => cat.products.length > 0);
  }, [categories, activeCategoryId, search]);

  const adjust = async (productId, payload) => {
    setBusyId(productId);
    setMsg(null);
    try {
      await api.post('/pos/inventory/adjust', { productId, ...payload });
      return true;
    } catch (error) {
      setMsg({ type: 'error', text: error.response?.data?.error || 'Could not update stock' });
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const refreshAfterOk = async (text) => {
    await loadInventory(storeId);
    setMsg({ type: 'ok', text });
  };

  const applyAdd = async (row) => {
    const addQty = Number(addQtys[row.id]);
    if (!Number.isFinite(addQty) || addQty <= 0) {
      setMsg({ type: 'error', text: 'Enter a positive quantity to add' });
      return;
    }
    const ok = await adjust(row.id, {
      delta: addQty,
      note: `Stock receive — ${row.category_name || 'category'}`
    });
    if (ok) {
      setAddQtys((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
      await refreshAfterOk(`Added ${addQty} to ${row.name}`);
    }
  };

  const applySet = async (row) => {
    const setQty = Number(drafts[row.id]);
    if (!Number.isFinite(setQty) || setQty < 0) {
      setMsg({ type: 'error', text: 'Enter a valid stock quantity' });
      return;
    }
    const ok = await adjust(row.id, { setQty, note: 'Set from inventory screen' });
    if (ok) await refreshAfterOk(`Set stock for ${row.name}`);
  };

  const applyDelta = async (row, delta) => {
    const ok = await adjust(row.id, { delta });
    if (ok) await refreshAfterOk('Stock updated');
  };

  const applyCategoryAdds = async (cat) => {
    const pending = cat.products
      .map((row) => ({ row, addQty: Number(addQtys[row.id]) }))
      .filter(({ addQty }) => Number.isFinite(addQty) && addQty > 0);

    if (!pending.length) {
      setMsg({ type: 'error', text: `Enter add quantities for items in ${cat.name}` });
      return;
    }

    setBusyCategoryId(cat.id);
    setMsg(null);
    let okCount = 0;
    try {
      for (const { row, addQty } of pending) {
        const ok = await adjust(row.id, {
          delta: addQty,
          note: `Stock receive — ${cat.name}`
        });
        if (!ok) break;
        okCount += 1;
        setAddQtys((prev) => {
          const next = { ...prev };
          delete next[row.id];
          return next;
        });
      }
      await loadInventory(storeId);
      if (okCount === pending.length) {
        setMsg({ type: 'ok', text: `Added stock for ${okCount} item${okCount === 1 ? '' : 's'} in ${cat.name}` });
      } else if (okCount > 0) {
        setMsg({
          type: 'error',
          text: `Updated ${okCount} of ${pending.length} items — stopped on an error`
        });
      }
    } finally {
      setBusyCategoryId(null);
    }
  };

  const selectedStore = stores.find((s) => String(s.id) === String(storeId));
  const anyBusy = busyId != null || busyCategoryId != null;

  return (
    <div className="pos-inv-page">
      <PageNavbar />
      <div className="pos-inv-main">
        <header className="pos-inv-header">
          <div>
            <h1>POS inventory</h1>
            <p>Add stock category by category. Sales hard-block when tracked stock runs out.</p>
          </div>
        </header>

        {msg ? (
          <div className={`pos-inv-msg ${msg.type === 'error' ? 'is-err' : 'is-ok'}`}>{msg.text}</div>
        ) : null}

        {loading ? (
          <p className="pos-inv-hint">Loading stores…</p>
        ) : (
          <div className="pos-inv-layout">
            <aside className="pos-inv-rail">
              <div className="pos-inv-rail-head">
                <h2>Stores</h2>
                <p>Choose an outlet</p>
              </div>
              <ul className="pos-inv-store-list">
                {stores.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      className={`pos-inv-store-card ${String(storeId) === String(s.id) ? 'is-active' : ''}`}
                      onClick={() => setStoreId(String(s.id))}
                    >
                      <strong>{s.name}</strong>
                      <span className="pos-inv-store-code">{s.code}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </aside>

            <section className="pos-inv-panel">
              <div className="pos-inv-panel-head">
                <h2>{selectedStore ? selectedStore.name : 'Inventory'}</h2>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={!storeId || listLoading || anyBusy}
                  onClick={() => loadInventory(storeId)}
                >
                  Refresh
                </button>
              </div>

              {listLoading ? (
                <p className="pos-inv-hint">Loading inventory…</p>
              ) : !items.length ? (
                <p className="pos-inv-hint">No products for this store yet. Add items under Store-wise menus.</p>
              ) : (
                <>
                  <div className="pos-inv-toolbar">
                    <div className="pos-inv-cats" role="tablist" aria-label="Categories">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={activeCategoryId === 'all'}
                        className={`pos-inv-cat-chip ${activeCategoryId === 'all' ? 'is-active' : ''}`}
                        onClick={() => setActiveCategoryId('all')}
                      >
                        All
                        <span className="pos-inv-cat-count">{items.length}</span>
                      </button>
                      {categories.map((cat) => (
                        <button
                          key={cat.id}
                          type="button"
                          role="tab"
                          aria-selected={String(activeCategoryId) === String(cat.id)}
                          className={`pos-inv-cat-chip ${
                            String(activeCategoryId) === String(cat.id) ? 'is-active' : ''
                          }`}
                          onClick={() => setActiveCategoryId(String(cat.id))}
                        >
                          {cat.name}
                          <span className="pos-inv-cat-count">{cat.products.length}</span>
                        </button>
                      ))}
                    </div>
                    <input
                      type="search"
                      className="pos-inv-search"
                      placeholder="Search item or code…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>

                  {!filteredCategories.length ? (
                    <p className="pos-inv-hint">No items match this filter.</p>
                  ) : (
                    <div className="pos-inv-cat-sections">
                      {filteredCategories.map((cat) => {
                        const pendingAdds = cat.products.filter((row) => {
                          const n = Number(addQtys[row.id]);
                          return Number.isFinite(n) && n > 0;
                        }).length;
                        const catBusy = busyCategoryId === cat.id;

                        return (
                          <section key={cat.id} className="pos-inv-cat-section">
                            <header className="pos-inv-cat-head">
                              <div>
                                <h3>{cat.name}</h3>
                                <p>
                                  {cat.products.length} item{cat.products.length === 1 ? '' : 's'}
                                  {pendingAdds ? ` · ${pendingAdds} ready to add` : ''}
                                </p>
                              </div>
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                disabled={anyBusy || !pendingAdds}
                                onClick={() => applyCategoryAdds(cat)}
                              >
                                {catBusy ? 'Adding…' : `Add stock for category${pendingAdds ? ` (${pendingAdds})` : ''}`}
                              </button>
                            </header>

                            <div className="pos-inv-table-wrap">
                              <table className="pos-inv-table">
                                <thead>
                                  <tr>
                                    <th>Item</th>
                                    <th>Code</th>
                                    <th>Price</th>
                                    <th>Current</th>
                                    <th>Add qty</th>
                                    <th>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {cat.products.map((row) => {
                                    const busy = busyId === row.id || catBusy;
                                    return (
                                      <tr key={row.id} className={row.low_stock ? 'is-low' : ''}>
                                        <td>
                                          <strong>{row.name}</strong>
                                          {!Number(row.track_stock) ? (
                                            <span className="pos-inv-badge">Not tracked</span>
                                          ) : null}
                                          {row.low_stock ? (
                                            <span className="pos-inv-badge pos-inv-badge--warn">Low</span>
                                          ) : null}
                                          {!Number(row.is_active) ? (
                                            <span className="pos-inv-badge">Inactive</span>
                                          ) : null}
                                        </td>
                                        <td>{row.shortcode || '—'}</td>
                                        <td>{formatInr(row.unit_price)}</td>
                                        <td>
                                          <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            className="pos-inv-stock-input"
                                            value={drafts[row.id] ?? ''}
                                            disabled={busy}
                                            aria-label={`Set stock for ${row.name}`}
                                            onChange={(e) =>
                                              setDrafts((d) => ({ ...d, [row.id]: e.target.value }))
                                            }
                                          />
                                        </td>
                                        <td>
                                          <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            className="pos-inv-stock-input pos-inv-stock-input--add"
                                            placeholder="0"
                                            value={addQtys[row.id] ?? ''}
                                            disabled={busy}
                                            aria-label={`Add stock for ${row.name}`}
                                            onChange={(e) =>
                                              setAddQtys((d) => ({ ...d, [row.id]: e.target.value }))
                                            }
                                          />
                                        </td>
                                        <td>
                                          <div className="pos-inv-actions">
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-primary"
                                              disabled={busy}
                                              onClick={() => applyAdd(row)}
                                            >
                                              Add
                                            </button>
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-secondary"
                                              disabled={busy}
                                              onClick={() => applyDelta(row, 1)}
                                            >
                                              +1
                                            </button>
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-secondary"
                                              disabled={busy || Number(row.stock_qty) <= 0}
                                              onClick={() => applyDelta(row, -1)}
                                            >
                                              −1
                                            </button>
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-secondary"
                                              disabled={busy}
                                              onClick={() => applySet(row)}
                                            >
                                              Set
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </section>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
